import { ServerCmdProviderBase } from './ServerCmdProviderBase.js';
import { ServerCommand, WriteHtmlMode } from './ServerCommand.js';
import { DefaultUiCommand } from '../imcmd/DefaultUiCommand.js';

// src/core/cmd/DefaultServerCmdProvider.ts
/**
 * Базовый (общий) провайдер серверных команд.
 *
 * Содержит набор универсальных кейсов, чтобы не раздувать адаптер.
 * По мере появления специализированных провайдеров — кейсы «переезжают» отсюда
 * в профильные реализации (view/table/media/...).
 */
class DefaultServerCmdProvider extends ServerCmdProviderBase {
    constructor() {
        super(...arguments);
        /** Уникальный ключ провайдера (для логов/отладки) */
        this.id = "default-server";
        /** Приоритет обработки: по умолчанию 0 (можно повысить/понизить при необходимости) */
        this.priority = 0;
    }
    /** Список поддерживаемых типов серверных команд */
    getSupported() {
        return Object.values(ServerCommand);
    }
    /**
     * Адаптирует одну серверную команду в набор UI-токенов.
     * Возвращает { tokens, consumed? }. Флаг consumed по умолчанию не ставим —
     * оставляем возможность совместной обработки несколькими провайдерами.
     *
     * @param cmd Данные серверной команды
     * @param _ctx Контекст выполнения UI-команд (не используется в дефолтной реализации)
     */
    adapt(cmd, _ctx) {
        const t = [];
        const maskStr = this.makeMaskStr(cmd.mask, cmd.area);
        switch (cmd.type) {
            // ───────────────────────────── базовые уведомления/навигация
            case ServerCommand.msg:
                t.push({
                    raw: "default::alert",
                    namespace: "default",
                    command: "alert",
                    rawArgs: [],
                    solvedArgs: {
                        title: cmd.title ?? "",
                        message: cmd.msg ?? "",
                        icon: cmd.icon,
                        css: cmd.css
                    },
                });
                break;
            case ServerCommand.redirect:
                t.push(`default::${DefaultUiCommand.redirect}|${cmd.url}|${cmd.newTab}`);
                break;
            // ───────────────────────────── view/visibility
            /** Показать элементы */
            case ServerCommand.show: {
                t.push(`default::${DefaultUiCommand.show}|${maskStr}|${cmd.speed ?? 0}`);
                break;
            }
            /** Скрыть элементы */
            case ServerCommand.hide: {
                t.push(`default::${DefaultUiCommand.hide}|${maskStr}|${cmd.speed ?? 0}`);
                break;
            }
            /** Поставить временный класс и убрать через задержку */
            case ServerCommand.tmpcss: {
                t.push(`default::${DefaultUiCommand.tempClass}|${maskStr}|${cmd.css}|${cmd.speed ?? 0}`);
                break;
            }
            // ───────────────────────────── tabs
            case ServerCommand.activeTab: {
                t.push(`default::${DefaultUiCommand.activeTab}|${maskStr}|${cmd.tabindex}`);
                break;
            }
            // ───────────────────────────── HTML/стили
            case ServerCommand.writeHtml: {
                // 1) Перехватить простые строчные команды и вернуть их в виде строк defautl::remove, setval setval:combo - есть ли поддержка 2го аргумента?
                const writeMode = cmd.mode ? cmd.mode : WriteHtmlMode.default;
                // Универсальная команда — вся ветвизация по mode внутри UI-провайдера                        
                switch (writeMode) {
                    case WriteHtmlMode.append:
                        t.push({
                            raw: `default::${DefaultUiCommand.append}|${maskStr}`,
                            namespace: "default",
                            command: DefaultUiCommand.append,
                            rawArgs: [],
                            solvedArgs: {
                                html: cmd.html,
                            },
                        });
                        break;
                    case WriteHtmlMode.prepend:
                        t.push({
                            raw: `default::${DefaultUiCommand.prepend}|${maskStr}`,
                            namespace: "default",
                            command: DefaultUiCommand.prepend,
                            rawArgs: [],
                            solvedArgs: {
                                html: cmd.html,
                            },
                        });
                        break;
                    case WriteHtmlMode.after:
                        t.push({
                            raw: `default::${DefaultUiCommand.after}|${maskStr}`,
                            namespace: "default",
                            command: DefaultUiCommand.after,
                            rawArgs: [],
                            solvedArgs: {
                                html: cmd.html,
                            },
                        });
                        break;
                    case WriteHtmlMode.replace:
                        t.push({
                            raw: `default::${DefaultUiCommand.replace}|${maskStr}`,
                            namespace: "default",
                            command: DefaultUiCommand.replace,
                            rawArgs: [],
                            solvedArgs: {
                                html: cmd.html,
                            },
                        });
                        break;
                    case WriteHtmlMode.remove:
                        t.push(`default::${DefaultUiCommand.remove}|${maskStr}`);
                        break;
                    case WriteHtmlMode.addClass:
                        t.push(`default::${DefaultUiCommand.addClass}|${maskStr}|${cmd.html}`);
                        break;
                    case WriteHtmlMode.removeClass:
                        t.push(`default::${DefaultUiCommand.removeClass}|${maskStr}|${cmd.html}`);
                        break;
                    case WriteHtmlMode.setval: {
                        t.push(`default::${DefaultUiCommand.setval}|${maskStr}|${cmd.html}|${!!cmd.sendevent}`);
                        break;
                    }
                    case WriteHtmlMode.setvalCombo: {
                        t.push(`default::${DefaultUiCommand.setval}|${maskStr}|${cmd.html}|${!!cmd.sendevent}|${cmd.text}`);
                        break;
                    }
                    case WriteHtmlMode.setattr: {
                        t.push(`default::${DefaultUiCommand.setAttribute}|${maskStr}|${cmd.attr}|${cmd.html}`);
                        break;
                    }
                    case WriteHtmlMode.moveUp: {
                        t.push(`default::${DefaultUiCommand.htmlMoveUp}|${maskStr}`);
                        break;
                    }
                    case WriteHtmlMode.moveDown: {
                        t.push(`default::${DefaultUiCommand.htmlMoveDown}|${maskStr}`);
                        break;
                    }
                    case WriteHtmlMode.default:
                        t.push({
                            raw: `default::${DefaultUiCommand.replaceInner}`,
                            namespace: "default",
                            command: DefaultUiCommand.replaceInner,
                            rawArgs: [],
                            solvedArgs: {
                                html: cmd.html, show: cmd.show, speed: cmd.speed
                            },
                        });
                        break;
                    default:
                        console.error(`Не поддерживается режим ${writeMode}`);
                        break;
                }
                break;
            }
            // ───────────────────────────── async/processes
            case ServerCommand.newWatchProcess: {
                t.push({
                    raw: "default::showProcess",
                    namespace: "default",
                    command: DefaultUiCommand.showProcess,
                    rawArgs: [],
                    solvedArgs: {
                        process: cmd.process, // id процесса
                        options: {
                            proc: cmd.proc,
                            updateInterval: cmd.updateInterval,
                            initText: cmd.initText,
                            contentHtml: cmd.contentHtml,
                            satusPosition: cmd.satusPosition,
                            cbi: cmd.cbi,
                            mode: cmd.mode,
                        },
                        modal: cmd.modal,
                        add: cmd.add,
                    },
                });
                break;
            }
            // ───────────────────────────── open form / dialog / panel tabs
            case ServerCommand.openform: {
                // Ветка прицельной вставки в контейнер по mask
                if (cmd.putInPlace) {
                    t.push({
                        raw: "default::openInPlace",
                        namespace: "default",
                        command: "openInPlace",
                        rawArgs: [],
                        solvedArgs: {
                            html: cmd.html,
                            prepend: !!cmd.prepend,
                            append: !cmd.prepend && !cmd.append ? false : !!cmd.append,
                        },
                    });
                    break;
                }
                // Ветви диалогов
                if (cmd.mode === "aside") {
                    t.push({
                        raw: "default::openAside",
                        namespace: "default",
                        command: "openAside",
                        rawArgs: [],
                        solvedArgs: {
                            title: cmd.title,
                            html: cmd.html,
                        },
                    });
                    break;
                }
                // Если modal == true, то открыть popup модальный, если есть Tabs, то открыть в табах
                // В ином случае открывам popup не модальный
                t.push({
                    raw: "default::openForm",
                    namespace: "default",
                    command: "openForm",
                    rawArgs: [],
                    solvedArgs: {
                        title: cmd.title,
                        html: cmd.html,
                        width: cmd.width,
                        height: cmd.height,
                        css: cmd.css,
                        modal: cmd.modal,
                        viewChanges: true,
                        description: ''
                    },
                });
                break;
            }
            // ───────────────────────────── события/скролл (уже были)
            case ServerCommand.event:
                t.push({
                    raw: "default::event",
                    namespace: "default",
                    command: "event",
                    rawArgs: [],
                    solvedArgs: { eventKey: cmd.eventKey, options: cmd.options },
                });
                break;
            case ServerCommand.scrollTo:
                t.push(`default::${DefaultUiCommand.scrollTo}|${cmd.mask}`);
                break;
        }
        return this.makeResult(t);
    }
}

export { DefaultServerCmdProvider };
